<script lang="ts" setup>
import type { MmIntelligenceItem } from '#public-research/utils/pr.utils'
import { rankBadgeColors } from '@forgd/shared/leaderboard/rankBadgeColors'
import { formatValue } from '@forgd/formatter'
import { Badge } from '@forgd/ui-kit/badge'
import { Button } from '@forgd/ui-kit/button'
import { ArrowRightIcon } from '@heroicons/vue/24/outline'

const TOP_COUNT = 3
const FALLBACK_COLOR = '#c7c7be'

const METRIC_LABELS = [
  'Composite Score',
  'Trading KPIs',
  'Trust',
  'Coverage & Capabilities',
  'Uptime',
  'Integration level',
] as const

function emptyCard(rank: number) {
  return {
    rank,
    name: '',
    imageUrl: null as string | null,
    activeEngagementsCount: null,
    accentColor: rankBadgeColors[rank] ?? FALLBACK_COLOR,
    metrics: METRIC_LABELS.map(label => ({ label, value: null })),
    bottomStats: [
      { label: 'Median FDV', value: 'N/A' },
      { label: 'Avg. 2.00% Depth', value: 'N/A', rank: null },
      { label: 'Avg. Spread', value: 'N/A', rank: null },
      { label: 'Avg. Volume', value: 'N/A', rank: null },
    ],
  }
}

const { performance, aggregatedById } = useLeaderboardPodiumData()

const analyzingCount = computed(() => (performance.value ?? []).length)

const topThree = computed<MmIntelligenceItem[]>(() => {
  const items: MmIntelligenceItem[] = (performance.value ?? []).slice(0, TOP_COUNT).map((mm, index) => {
    const rank = index + 1
    const agg = aggregatedById.value.get(mm.marketMakerId)
    return {
      rank,
      name: mm.marketMakerName,
      imageUrl: mm.marketMakerImageUrl,
      activeEngagementsCount: mm.activeEngagementsCount,
      accentColor: rankBadgeColors[rank] ?? FALLBACK_COLOR,
      metrics: [
        { label: 'Composite Score', value: mm.composite },
        { label: 'Trading KPIs', value: mm.tradingKpis },
        { label: 'Trust', value: mm.trustIntegration },
        { label: 'Coverage & Capabilities', value: mm.coverageCapabilities },
        { label: 'Uptime', value: mm.uptime },
        { label: 'Integration level', value: mm.integrationLevel },
      ],
      bottomStats: [
        { label: 'Median FDV', value: formatValue(mm.medianEngagementsFdv, 'currency:short,2') },
        { label: 'Avg. 2.00% Depth', value: formatValue(agg?.bidAskDepth.value, 'currency:short,2'), rank: agg?.bidAskDepth.rank },
        { label: 'Avg. Spread', value: formatValue(agg?.topOfBookSpread.value, 'percent:2'), rank: agg?.topOfBookSpread.rank },
        { label: 'Avg. Volume', value: formatValue(agg?.dailyFillVolume.value, 'currency:short,2'), rank: agg?.dailyFillVolume.rank },
      ],
    }
  })
  while (items.length < TOP_COUNT) {
    items.push(emptyCard(items.length + 1))
  }
  return items
})
</script>

<template>
  <section class="flex flex-col gap-5">
    <header class="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-5">
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-2">
          <h2 class="text-sm font-semibold text-primal">
            Market Maker Intelligence
          </h2>
          <Badge v-if="analyzingCount" variant="primary" size="sm">
            <span class="hidden sm:inline">Analyzing&nbsp;</span>{{ formatValue(analyzingCount, 'string:plural,Market Maker') }}
          </Badge>
        </div>
        <p class="text-xs text-secondary">
          Composite scoring, depth, spreads, volume & KPI adherence.
        </p>
      </div>
      <Button as-child variant="outline" size="sm" class="self-start sm:self-end">
        <NuxtLink to="/market-maker-leaderboard">
          View the Leaderboards
          <ArrowRightIcon aria-hidden="true" />
        </NuxtLink>
      </Button>
    </header>

    <div class="grid grid-cols-1 items-stretch gap-5 lg:grid-cols-3">
      <PrOverviewMmIntelligenceCard
        v-for="card in topThree"
        :key="card.rank"
        v-bind="card"
      />
    </div>
  </section>
</template>
